import React, { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  findAbhaByMobile,
  getUserDetails,
  requestFindAbhaOtp,
  setFindAbhaUserDetails,
  verifyFindAbhaOtp,
} from "../../../../store/slices/abhaRegistrationSlice";
import {
  MAX_OTP_RESEND_ATTEMPTS,
  getOtpResendAttemptMessage,
} from "./otpResendAttempts";
import styles from "./AdharRegistrartion.module.scss";

const OTP_RESEND_TIMEOUT = 60;

const getList = (payload) => payload?.result?.list || payload || {};

const getErrorMessage = (response, fallback) => {
  const payload = response?.payload;
  if (typeof payload === "string") return payload;
  return (
    payload?.message ||
    payload?.result?.message ||
    payload?.error?.message ||
    response?.error?.message ||
    fallback
  );
};

const normalizeFindAbhaResult = (payload) => {
  const list = getList(payload);
  const searchResult = Array.isArray(list) ? list[0] : list;
  return {
    txnId: searchResult?.txnId || "",
    accounts: (
      searchResult?.ABHA ||
      searchResult?.abha ||
      searchResult?.accounts ||
      []
    ).map((account) => ({
      ...account,
      txnId: searchResult?.txnId || account?.txnId || "",
    })),
  };
};

const normalizeVerifiedUserDetails = (payload) => {
  const list = getList(payload);
  const source = Array.isArray(list) ? list[0] : list;
  const details =
    source?.profile ||
    source?.userDetails ||
    source?.abhaProfile ||
    source?.ABHAProfile ||
    source?.account ||
    source?.patient ||
    source?.details ||
    source;

  if (!details || Array.isArray(details)) return {};

  return {
    ...details,
    ABHANumber:
      details?.ABHANumber ||
      details?.abhaNumber ||
      details?.abha_number ||
      details?.healthIdNumber ||
      "",
    abhaAddress:
      details?.preferredAbhaAddress ||
      details?.abhaAddress ||
      details?.ABHAAddress ||
      details?.phrAddress ||
      details?.healthId ||
      "",
    mobile: details?.mobile || details?.mobileNumber || details?.phone || "",
  };
};

const FindAbha = ({
  clinicId,
  setModalVisible,
  setShowRegistrationModal,
  onBack,
}) => {
  const dispatch = useDispatch();
  const { token } = useSelector((state) => state.auth);
  const { loading } = useSelector((state) => state.abhaRegistration);
  const [mobileNumber, setMobileNumber] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [showOtpStep, setShowOtpStep] = useState(false);
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [timer, setTimer] = useState(20);
  const [resendCount, setResendCount] = useState(0);
  const [maxAttemptsReached, setMaxAttemptsReached] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    let interval;
    if (showOtpStep && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [showOtpStep, timer]);

  const formatTimer = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 10) {
      setMobileNumber(value);
      setErrorMessage("");
    }
  };

  const handleSearch = async () => {
    if (mobileNumber.length !== 10) {
      setErrorMessage("Please enter a valid 10-digit mobile number.");
      return;
    }

    setErrorMessage("");
    setMessage("");
    setSelectedAccount(null);
    setShowOtpStep(false);

    const response = await dispatch(
      findAbhaByMobile({ clinicId, mobile: mobileNumber, token })
    );

    if (!response?.payload?.success) {
      setAccounts([]);
      setErrorMessage(
        getErrorMessage(response, "No ABHA profile found for this mobile number.")
      );
      return;
    }

    const result = normalizeFindAbhaResult(response.payload);
    setAccounts(result.accounts);
    if (!result.accounts?.length) {
      setErrorMessage("No ABHA profile found for this mobile number.");
    }
  };

  const requestOtp = async (account, isResend = false) => {
    const response = await dispatch(
      requestFindAbhaOtp({
        clinicId,
        index: account?.index,
        txnId: account?.txnId,
        scopeType: "search-abha",
        otpSystem: "abdm",
        token,
      })
    );

    if (!response?.payload?.success) {
      setErrorMessage(getErrorMessage(response, "Unable to send OTP. Please try again."));
      return false;
    }

    const list = getList(response.payload);
    setMessage(list?.message || "OTP sent to registered mobile number.");
    setOtp(["", "", "", "", "", ""]);
    setTimer(OTP_RESEND_TIMEOUT);
    setShowOtpStep(true);
    setErrorMessage("");
    if (!isResend) {
      setResendCount(0);
      setMaxAttemptsReached(false);
    }
    return true;
  };

  const handleSelectAccount = async (account) => {
    setSelectedAccount(account);
    await requestOtp(account);
  };

  const handleResendOtp = async () => {
    if (timer > 0 || resendCount >= MAX_OTP_RESEND_ATTEMPTS || !selectedAccount) {
      return;
    }
    const sent = await requestOtp(selectedAccount, true);
    if (sent) {
      const nextCount = resendCount + 1;
      setResendCount(nextCount);
      if (nextCount >= MAX_OTP_RESEND_ATTEMPTS) {
        setMaxAttemptsReached(true);
      }
    }
  };

  const handleOtpChange = (e, index) => {
    setErrorMessage("");
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 1) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value && index < 5) inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    const enteredOtp = otp.join("");
    if (enteredOtp.length !== 6) {
      setErrorMessage("Please enter a valid 6-digit OTP.");
      return;
    }

    const response = await dispatch(
      verifyFindAbhaOtp({ clinicId, otp: enteredOtp, token })
    );

    if (response?.payload?.success) {
      const verifiedDetails = normalizeVerifiedUserDetails(response.payload);
      const userDetailsResponse = await dispatch(
        getUserDetails({ clinicId, token })
      );
      const fullUserDetails = userDetailsResponse?.payload?.success
        ? normalizeVerifiedUserDetails(userDetailsResponse.payload)
        : {};

      dispatch(
        setFindAbhaUserDetails({
          name: selectedAccount?.name || "",
          gender: selectedAccount?.gender || "",
          mobile: mobileNumber,
          ABHANumber: selectedAccount?.ABHANumber?.includes("x")
            ? ""
            : selectedAccount?.ABHANumber || "",
          ...verifiedDetails,
          ...fullUserDetails,
        })
      );
      setShowRegistrationModal(false);
      setModalVisible(true);
      return;
    }

    setErrorMessage(getErrorMessage(response, "Invalid OTP. Please try again."));
  };

  return (
    <div className={styles.findAbhaWrapper}>
      {!showOtpStep && (
        <>
          <div className="form-group">
            <label className="fw-bold">Mobile Number:</label>
            <input
              type="text"
              className="form-control"
              value={mobileNumber}
              onChange={handleMobileChange}
              maxLength={10}
              placeholder="Enter mobile number"
            />
          </div>

          {errorMessage && <p className="text-danger small mt-2">{errorMessage}</p>}

          <div className="text-end mt-3">
            {onBack && (
              <Button
                variant="outline-dark"
                className="me-2"
                onClick={onBack}
                disabled={loading}
              >
                Back
              </Button>
            )}
            <Button variant="primary" onClick={handleSearch} disabled={loading}>
              {loading ? "Searching..." : "Search ABHA"}
            </Button>
          </div>

          {!!accounts?.length && (
            <div className="mt-4">
              <h5 className="fw-bold mb-3">ABHA Profiles</h5>
              <div className="table-responsive">
                <table className="table table-bordered align-middle">
                  <thead>
                    <tr>
                      <th>Select</th>
                      <th>ABHA Number</th>
                      <th>Name</th>
                      <th>Gender</th>
                      <th>KYC</th>
                      <th>Auth Methods</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.map((account) => (
                      <tr key={account.index || account.ABHANumber}>
                        <td>
                          <input
                            type="radio"
                            name="findAbhaAccount"
                            checked={selectedAccount?.index === account.index}
                            onChange={() => handleSelectAccount(account)}
                          />
                        </td>
                        <td>{account.ABHANumber || "-"}</td>
                        <td>{account.name || "-"}</td>
                        <td>{account.gender || "-"}</td>
                        <td>{String(account.kycVerified || "-")}</td>
                        <td>{account.authMethods?.join(", ") || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {showOtpStep && (
        <div className={styles.adhaarDiv}>
          <p>Confirm OTP</p>
          <label className="col-form-label">{message}</label>
          <div className="d-flex gap-2 mt-2">
            {otp.map((digit, index) => (
              <input
                key={index}
                type="text"
                className="form-control text-center"
                value={digit}
                onChange={(e) => handleOtpChange(e, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                maxLength={1}
                ref={(el) => (inputRefs.current[index] = el)}
                style={{ width: "4rem" }}
              />
            ))}
          </div>

          {errorMessage && <p className="text-danger small mt-2">{errorMessage}</p>}
          {resendCount > 0 && (
            <p
              className={`small mt-2 mb-0 ${
                resendCount >= MAX_OTP_RESEND_ATTEMPTS
                  ? "text-danger"
                  : "text-success"
              }`}
            >
              {getOtpResendAttemptMessage(resendCount)}
            </p>
          )}

          <div className="mt-2">
            {!maxAttemptsReached ? (
              <>
                <small className="fs-13">Didn't receive the OTP?</small>{" "}
                <small
                  onClick={handleResendOtp}
                  style={{
                    cursor: timer > 0 ? "not-allowed" : "pointer",
                    color: timer > 0 ? "gray" : "blue",
                    marginLeft: "0.5rem",
                  }}
                >
                  Resend OTP
                </small>
                <span className="ms-3">Time left: {formatTimer(timer)}</span>
              </>
            ) : (
              <div className="text-black fs-13">
                Resend OTP limit reached. Please try again later.
              </div>
            )}
          </div>

          <div className="text-end mt-3">
            <Button
              variant="outline-dark"
              className="me-2"
              onClick={() => setShowOtpStep(false)}
              disabled={loading}
            >
              Back
            </Button>
            <Button variant="success" onClick={handleVerifyOtp} disabled={loading}>
              {loading ? "Verifying..." : "Verify OTP"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FindAbha;
